import { v4 as uuidv4 } from 'uuid';
import { AbstractTranscriptEditorCommand } from './AbstractTranscriptEditorCommand';
import { TranscriptEditorCore } from '../TranscriptEditorCore';
import { IBatchable } from './interfaces/IBatchable';

/**
 * Command that executes multiple other commands as a single operation
 * 
 * This enables atomic execution of related operations that should be undone
 * or redone together. If any command in the batch fails, the entire batch
 * is rolled back.
 */
export class BatchCommand extends AbstractTranscriptEditorCommand {
  /** Commands included in this batch */
  private commands: AbstractTranscriptEditorCommand[];
  
  /** Custom description for the batch */
  private batchDescription: string;
  
  /**
   * Creates a new batch command
   * 
   * @param editor Reference to the transcript editor
   * @param commands Array of commands to include in the batch
   * @param description Optional custom description for the batch
   */
  constructor(editor: TranscriptEditorCore, commands: AbstractTranscriptEditorCommand[], description?: string) {
    // Create default description based on command count
    const defaultDescription = `Batch of ${commands.length} commands`;
    
    // Initialize with parent class
    super(description || defaultDescription, editor);
    
    // Store local properties
    this.commands = [...commands];
    this.batchDescription = description || defaultDescription;
    this.setBatchId(uuidv4());
    
    // Mark commands as part of this batch if they support it
    this.commands.forEach((command, index) => {
      if (this.isBatchable(command)) {
        const batchable = command as AbstractTranscriptEditorCommand & IBatchable;
        batchable.setBatchId(this.getBatchId()!);
        batchable.setBatchPosition(index);
      }
    });
  }
  
  /**
   * Type guard to check if a command implements IBatchable
   * 
   * @param command The command to check
   * @returns True if the command is batchable
   */
  private isBatchable(command: any): command is IBatchable {
    return 'setBatchId' in command && 
           'setBatchPosition' in command &&
           'getBatchId' in command &&
           'getBatchPosition' in command;
  }
  
  /**
   * Executes all commands in the batch
   * 
   * If any command fails, all previously executed commands are undone.
   * 
   * @returns Promise resolving to true if all commands executed successfully
   */
  async execute(): Promise<boolean> {
    try {
      // Execute all commands in sequence
      for (let i = 0; i < this.commands.length; i++) {
        const command = this.commands[i];
        const success = await Promise.resolve(command.execute());
        
        if (!success) {
          console.warn(`Command ${i} in batch "${this.getDescription()}" failed, rolling back...`);
          
          // If any command fails, roll back all previous commands
          for (let j = i - 1; j >= 0; j--) {
            await Promise.resolve(this.commands[j].undo());
          }
          
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error(`Error executing batch "${this.getDescription()}":`, error);
      return false;
    }
  }
  
  /**
   * Undoes all commands in the batch in reverse order
   * 
   * If any undo fails, all previously undone commands are re-executed.
   * 
   * @returns Promise resolving to true if all commands were undone successfully
   */
  async undo(): Promise<boolean> {
    try {
      // Undo all commands in reverse order
      for (let i = this.commands.length - 1; i >= 0; i--) {
        const success = await Promise.resolve(this.commands[i].undo());
        
        if (!success) {
          console.warn(`Undo of command ${i} in batch "${this.getDescription()}" failed, rolling forward...`);
          
          // If any undo fails, re-execute all previously undone commands
          for (let j = i + 1; j < this.commands.length; j++) {
            await Promise.resolve(this.commands[j].execute());
          }
          
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error(`Error undoing batch "${this.getDescription()}":`, error);
      return false;
    }
  }
  
  /**
   * Gets a description of the batch command
   * 
   * @returns A description of the batch command
   */
  override getDescription(): string {
    return this.batchDescription;
  }
  
  /**
   * Gets the number of commands in this batch
   * 
   * @returns Number of commands
   */
  getCommandCount(): number {
    return this.commands.length;
  }
  
  // getBatchId method is inherited from AbstractTranscriptEditorCommand
  
  /**
   * Gets all commands in this batch
   * 
   * @returns Array of commands
   */
  getCommands(): AbstractTranscriptEditorCommand[] {
    return [...this.commands];
  }
  
  /**
   * Indicates whether this batch requires sorting after execution
   * Returns true if any command in the batch requires sorting
   * 
   * @returns True if any command in the batch requires sorting
   */
  override requiresSorting(): boolean {
    // Check if any command in the batch requires sorting
    return this.commands.some(command => 
      command.requiresSorting && command.requiresSorting()
    );
  }
}